import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * Closes the short race between a user's first activation and the moment a
 * component's own loading/disabled state reaches the DOM.
 *
 * This is intentionally opt-in: controls such as steppers, sliders, and media
 * navigation must remain repeatable. Existing loading flags remain responsible
 * for locking long-running work.
 */
@Directive({
  selector: '[appPreventDoubleAction]',
  standalone: true
})
export class PreventDoubleActionDirective implements OnInit, OnDestroy {
  @Input() appPreventDoubleAction: number | string = 900;

  private locked = false;
  private unlockTimer?: ReturnType<typeof setTimeout>;
  private previousAriaBusy: string | null = null;

  private readonly captureClick = (event: Event): void => {
    if (event instanceof MouseEvent && event.button !== 0) {
      return;
    }

    const element = this.elementRef.nativeElement;
    if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
      return;
    }

    if (this.locked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      return;
    }

    this.locked = true;
    this.previousAriaBusy = element.getAttribute('aria-busy');
    this.renderer.addClass(element, 'app-action-locked');
    this.renderer.setAttribute(element, 'aria-busy', 'true');

    this.zone.runOutsideAngular(() => {
      this.unlockTimer = setTimeout(() => this.unlock(), this.resolveDelay());
    });
  };

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone
  ) {}

  ngOnInit(): void {
    // Capture phase runs before Angular's generated click listener, so the
    // second activation cannot enter the component handler.
    this.elementRef.nativeElement.addEventListener('click', this.captureClick, true);
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('click', this.captureClick, true);
    this.unlock();
  }

  private resolveDelay(): number {
    const configuredDelay = Number(this.appPreventDoubleAction);
    if (!Number.isFinite(configuredDelay) || configuredDelay <= 0) {
      return 900;
    }

    return Math.min(Math.max(configuredDelay, 250), 5000);
  }

  private unlock(): void {
    if (this.unlockTimer) {
      clearTimeout(this.unlockTimer);
      this.unlockTimer = undefined;
    }

    if (!this.locked) {
      return;
    }

    const element = this.elementRef.nativeElement;
    this.locked = false;
    this.renderer.removeClass(element, 'app-action-locked');

    if (this.previousAriaBusy === null) {
      this.renderer.removeAttribute(element, 'aria-busy');
    } else {
      this.renderer.setAttribute(element, 'aria-busy', this.previousAriaBusy);
    }
    this.previousAriaBusy = null;
  }
}
